import React, { useEffect, useState } from 'react';
import { Form, Input, Select, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import './HomeAddressEdit.scss';
import DrawerFormContainer from 'components/drawerFormContainer/DrawerFormContainer';
import { fetchAndUpdateProfileWithPartialProperties } from 'services/profileService';
import { fetchDistrict } from 'services/profileService';
import { LoyaltyAccount } from 'types/dto/profile';
import { convertObjectToOptions } from 'utils/commonUtils';
import { CommonOption } from 'types/common';
import i18n from 'i18n';
// import { useSession } from 'context/SessionContext';
import { SecurityUtils } from 'utils/securityUtils';

interface HomeAddressEditProps {
  visible: boolean;
  onClose: () => void;
  onSave?: () => void;
  loyaltyAccount?: LoyaltyAccount;
}

export interface HomeAddressFormData {
  address: string;
  district: string;
}

const HomeAddressEdit: React.FC<HomeAddressEditProps> = ({
  visible,
  onClose,
  onSave,
  loyaltyAccount,
}) => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [districtOptions, setDistrictOptions] = useState<CommonOption[]>([]);
  const lang = i18n.language;
  const isEnglish = lang === 'en';
  // const { session } = useSession();

  const hasAddress = loyaltyAccount?.address && loyaltyAccount?.address !== '';

  // Fetch district options
  useEffect(() => {
    const initData = async () => {
      try {
        const districtData = await fetchDistrict('/common', lang);
        setDistrictOptions(convertObjectToOptions(districtData));
      } catch (err) {
        console.error('Failed to fetch districts:', err);
      }
    };
    initData();
  }, [lang]);

  const handleValuesChange = (changedValues: any) => {
    const fieldNames = Object.keys(changedValues);
    if (fieldNames.length > 0) {
      const fieldsToUpdate = fieldNames.map(name => ({
        name: name as any,
        errors: [],
      }));
      form.setFields(fieldsToUpdate);
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      // console.log('Home address saved:', values);

      await fetchAndUpdateProfileWithPartialProperties(loyaltyAccount?.unique_id || '',  {
        address: values.address,
        district: values.district,
      });

      onSave?.();
    } catch (error) {
      console.error('Validation failed:', error);
      notification.error({
        message: tCommon('error.updateHomeAddressFailed'),
        description: (error as Error).message,
        duration: 1.5,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Address validation rules
  const addressValidationRules = [
    {
      required: true,
      message: t('homeAddressEdit.addressRequired'),
    },
  ];

  // District validation rules
  const districtValidationRules = [
    {
      required: true,
      message: t('homeAddressEdit.districtRequired'),
    },
  ];

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({
        address: loyaltyAccount?.address || '',
        district: (isEnglish ? loyaltyAccount?.district_en : loyaltyAccount?.district_tc) || '',
      });
    }
  }, [form, visible, loyaltyAccount, isEnglish]);

  return (
    <DrawerFormContainer
      title={
        hasAddress
          ? t('homeAddressEdit.editAddress')
          : t('homeAddressEdit.title')
      }
      visible={visible}
      onClose={() => {
        onClose();
      }}
      onSave={handleSave}
      saveButtonTitle={tCommon('button.save')}
      isSubmitting={isSubmitting}
    >
      <Form form={form} layout="vertical" className="home-address-form" validateTrigger="onSubmit" onValuesChange={handleValuesChange}>
        <Form.Item
          name="address"
          label={t('homeAddressEdit.addressLabel')}
          getValueFromEvent={(e) => SecurityUtils.sanitize(e.target.value)}
          rules={[
            ...(addressValidationRules || []),
            {
              max: 200,
              message: t('homeAddressEdit.addressMaxError'),
            },
          ]}
        >
          <Input
            placeholder={t('homeAddressEdit.addressPlaceholder')}
            // maxLength={200}
            className="form-input address-input"
          />
        </Form.Item>
        <Form.Item
          name="district"
          label={t('homeAddressEdit.districtLabel')}
          rules={districtValidationRules}
        >
          <Select
            placeholder={t('homeAddressEdit.districtPlaceholder')}
            options={districtOptions}
            optionFilterProp="label"
            showSearch
            className="form-select district-select"
          />
        </Form.Item>
      </Form>
    </DrawerFormContainer>
  );
};

export default HomeAddressEdit;